import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { postData, fetchData } from '../../../apiService';
import Select from 'react-select';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444',
};

const inp  = { border: `1px solid ${C.cardBorder}`, borderRadius: '7px', fontSize: '12px', height: '28px', width: '100%', padding: '0 8px', outline: 'none', backgroundColor: '#f8fafa', boxSizing: 'border-box', fontFamily: 'inherit' };
const inpR = { ...inp, textAlign: 'right' };
const card = { backgroundColor: C.card, borderRadius: '10px', boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, marginBottom: '10px', overflow: 'hidden' };
const cardHead = (color = C.accent) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderBottom: `1px solid ${C.cardBorder}`, backgroundColor: `${color}18`, fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.5px' });
const cardBody = { padding: '10px 12px' };
const lbl = { fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '3px' };

const selectStyles = {
  control: (base) => ({ ...base, minHeight: '28px', height: '28px', fontSize: '12px', borderRadius: '7px', borderColor: C.cardBorder }),
  valueContainer: (base) => ({ ...base, height: '28px', padding: '0 6px' }),
  indicatorsContainer: (base) => ({ ...base, height: '28px' }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  menu: (base) => ({ ...base, fontSize: '12px' }),
};

const PaymentAndReceiptCreate = () => {
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const { isAuthenticated, companyId, branchId, userId, logout, token } = useAuth();

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage]     = useState('');

  const [customerOptions, setCustomerOptions]           = useState([]);
  const [selectedCustomer, setSelectedCustomer]         = useState(null);
  const [paymentmethodOptions, setPaymentmethodOptions] = useState([]);
  const [selectedPaymentmethodType, setSelectedPaymentmethodType] = useState(null);
  const [bankOptions, setBankOptions]   = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [showBankField, setShowBankField] = useState(false);

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const [formData, setFormData] = useState({
    CompanyId: companyId, BranchId: branchId,
    BillDate: '', BillNumber: 0, FinYear: '',
    MasterId: '', CreatedBy: userId,
    Amount: '', Remarks: '', IsType: true,
    PaymentType: 1, ChequeNumber: '', ChequeDate: '', chequeDetails: '',
    GSTRate: 0, IsIGST: false,
    CGSTAmount: '0.00', SGSTAmount: '0.00', IGSTAmount: '0.00', RoundOff: '0.00', TotalAmount: '0.00',
  });

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchmaxbillno(true);
    fetchPaymentmethodTypes();
    fetchBanks();
    fetchCustomerData();
    const today = formatDateForInput(new Date());
    setFormData(prev => ({ ...prev, BillDate: today, ChequeDate: today }));
  }, []);

  const fetchmaxbillno = (isType) => {
    fetchData('PaymentAndReceiptApi', `/MaxBillno/${branchId}/${companyId}/${isType}`, token)
      .then(res => res.response_data)
      .then(data => {
        const today = formatDateForInput(new Date());
        setFormData(prev => ({ ...prev, BillNumber: data.BillNumber, BillDate: today, FinYear: data.FinYear, BranchId: branchId, CompanyId: companyId }));
      })
      .catch(err => console.error('Error fetching max bill no:', err));
  };

  const fetchPaymentmethodTypes = async () => {
    try {
      const res = await fetchData('PaymentApi', `/PaymentModes/${companyId}`, token);
      if (res.responseCode === '1111') setPaymentmethodOptions(res.response_data.map(m => ({ value: m.Id, label: m.Name })));
    } catch (err) { console.error('Error fetching payment modes:', err); }
  };

  const fetchBanks = async () => {
    try {
      const res = await fetchData('PaymentApi', `/Banks/${companyId}`, token);
      if (res.responseCode === '1111') setBankOptions(res.response_data.map(m => ({ value: m.Id, label: m.Name })));
    } catch (err) { console.error('Error fetching banks:', err); }
  };

  const fetchCustomerData = () => {
    fetchData('PaymentAndReceiptApi', `/Party/${companyId}`, token)
      .then(res => res.response_data)
      .then(data => setCustomerOptions(data.map(c => ({ label: c.Name, value: c.Id }))))
      .catch(err => console.error('Error fetching parties:', err));
  };

  const handleCustomerSelect = (selected) => {
    setSelectedCustomer(selected);
    setFormData(prev => ({ ...prev, MasterId: selected?.value || '', CreatedBy: userId }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'IsType') {
      const isPayment = value === '1';
      setFormData(prev => ({ ...prev, IsType: isPayment }));
      fetchmaxbillno(isPayment);
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handlePaymentTypeChange = (selected) => {
    setSelectedPaymentmethodType(selected);
    setFormData(prev => ({ ...prev, PaymentType: selected.value }));
    setShowBankField(['Cheque', 'RTGS', 'NEFT', 'UPI'].includes(selected.label));
  };

  const handleBankChange = (selected) => { setSelectedBank(selected); };

  const parseAndFix = (val) => parseFloat(val) || 0;

  useEffect(() => {
    const amt = parseAndFix(formData.Amount);
    const rate = parseAndFix(formData.GSTRate);
    let cgst = 0, sgst = 0, igst = 0;
    if (formData.IsIGST) { igst = ((amt * rate) / 100).toFixed(2); }
    else { cgst = ((amt * (rate / 2)) / 100).toFixed(2); sgst = ((amt * (rate / 2)) / 100).toFixed(2); }
    const netAmount = parseAndFix(amt) + parseAndFix(igst) + parseAndFix(cgst) + parseAndFix(sgst);
    const roundOff = Math.round(netAmount) - netAmount;
    const grandTotal = netAmount + roundOff;
    setFormData(prev => ({ ...prev, CGSTAmount: parseFloat(cgst).toFixed(2), SGSTAmount: parseFloat(sgst).toFixed(2), IGSTAmount: parseFloat(igst).toFixed(2), RoundOff: parseFloat(roundOff).toFixed(2), TotalAmount: parseFloat(grandTotal).toFixed(2) }));
  }, [formData.Amount, formData.GSTRate, formData.IsIGST]);

  const submitData = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (!formData.MasterId) { setErrorMessage('Master is required!'); setIsLoading(false); return; }
    if (!formData.Amount || formData.Amount === 0) { setErrorMessage('Amount must be greater than 0!'); setIsLoading(false); return; }
    if (selectedPaymentmethodType && selectedPaymentmethodType.label !== 'Cash') {
      if (!formData.ChequeNumber) { setErrorMessage('Reference No is required!'); setIsLoading(false); return; }
      if (!formData.ChequeDate)   { setErrorMessage('Reference Date is required!'); setIsLoading(false); return; }
      if (!formData.chequeDetails){ setErrorMessage('Reference Details are required!'); setIsLoading(false); return; }
      if (!selectedBank)          { setErrorMessage('Bank selection is required!'); setIsLoading(false); return; }
    }
    try {
      await postData('PaymentAndReceiptApi', { ...formData, CreatedBy: userId, BankId: selectedBank?.value || null }, token);
      setSuccessMessage('Payment & Receipt Created Successfully!');
      setIsLoading(false);
      history.push('/PaymentAndReceiptIndex');
    } catch (err) {
      console.error('Error creating:', err);
      setErrorMessage('Failed to create. Please try again.');
      setIsLoading(false);
    }
  };

  const isCashPayment = !selectedPaymentmethodType || selectedPaymentmethodType.label === 'Cash';

  return (
    <div style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: '12px', width: '100%', padding: '6px', boxSizing: 'border-box', backgroundColor: C.bg }}>

      {/* ── Header ── */}
      <div style={{ ...card, overflow: 'visible' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <SwapHorizIcon style={{ fontSize: 16, color: C.accent }} />
            <span style={{ fontWeight: 800, fontSize: '13px', color: C.text, fontFamily: 'Inter, sans-serif' }}>Create Payment &amp; Receipt</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: '#3b82f6', cursor: 'pointer' }}>
              <input type="radio" name="IsType" value="1" checked={formData.IsType === true} onChange={handleChange} /> Payment
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: '#a855f7', cursor: 'pointer' }}>
              <input type="radio" name="IsType" value="0" checked={formData.IsType === false} onChange={handleChange} /> Receipt
            </label>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={submitData} disabled={isLoading}
              style={{ height: '28px', padding: '0 16px', borderRadius: '7px', border: 'none', backgroundColor: C.accent, color: '#fff', fontWeight: 700, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'inherit' }}>
              {isLoading ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : '✓ Save'}
            </button>
            <Link to={{ pathname: '/PaymentAndReceiptIndex', state: { from: '/PaymentAndReceiptCreate' } }} onClick={() => localStorage.setItem('shouldReload', 'true')}
              style={{ height: '28px', padding: '0 14px', borderRadius: '7px', border: `1px solid ${C.cardBorder}`, backgroundColor: C.card, color: C.muted, fontWeight: 600, fontSize: '11px', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              ← Back
            </Link>
          </div>
        </div>
      </div>

      {errorMessage   && <div style={{ marginBottom: '8px', padding: '6px 12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '7px', fontSize: '11px', color: C.danger }}>{errorMessage}</div>}
      {successMessage && <div style={{ marginBottom: '8px', padding: '6px 12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '7px', fontSize: '11px', color: '#16a34a' }}>{successMessage}</div>}

      {/* ── Entry Details ── */}
      <div style={card}>
        <div style={cardHead()}>📋 Entry Details</div>
        <div style={{ ...cardBody, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={lbl}>Master <span style={{ color: C.danger }}>*</span></label>
            <Select value={selectedCustomer} onChange={handleCustomerSelect} options={customerOptions}
              placeholder="Select Master…" isSearchable menuPortalTarget={document.body} styles={selectStyles} />
          </div>
          <div>
            <label style={lbl}>Date <span style={{ color: C.danger }}>*</span></label>
            <input type="date" name="BillDate" value={formatDateForInput(formData.BillDate)} onChange={handleChange} style={inp} />
          </div>
          <div>
            <label style={lbl}>Bill Number</label>
            <input type="text" value={`${formData.BillNumber || ''}/${formData.FinYear || ''}`} readOnly style={{ ...inp, backgroundColor: '#f0f4f4', cursor: 'default' }} />
          </div>
          <div>
            <label style={lbl}>Amount <span style={{ color: C.danger }}>*</span></label>
            <input type="number" name="Amount" value={formData.Amount} onChange={handleChange} style={inpR} />
          </div>
          <div>
            <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}>
              GST % <input type="checkbox" name="IsIGST" checked={formData.IsIGST || false} onChange={handleChange} />
              <span style={{ color: C.danger, fontSize: '9px' }}>IGST</span>
            </label>
            <select name="GSTRate" value={formData.GSTRate || 0} onChange={handleChange} style={{ ...inp, cursor: 'pointer' }}>
              {[0, 3, 5, 12, 18, 28].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Remarks</label>
            <input type="text" name="Remarks" value={formData.Remarks || ''} onChange={handleChange} placeholder="Remarks" style={inp} />
          </div>
        </div>
        {/* GST summary bar */}
        <div style={{ display: 'flex', gap: '20px', padding: '6px 12px', borderTop: `1px solid ${C.cardBorder}`, backgroundColor: '#f8fafa', flexWrap: 'wrap' }}>
          {[['CGST', formData.CGSTAmount], ['SGST', formData.SGSTAmount], ['IGST', formData.IGSTAmount], ['R.Off', formData.RoundOff], ['Total', formData.TotalAmount]].map(([label, val]) => (
            <div key={label} style={{ fontSize: '11px', color: C.muted }}>{label}: <strong style={{ color: '#B71C1C' }}>{val}</strong></div>
          ))}
        </div>
      </div>

      {/* ── Payment Details ── */}
      <div style={card}>
        <div style={cardHead('#f59e0b')}>💳 Payment Details</div>
        <div style={{ ...cardBody, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
          <div>
            <label style={lbl}>Payment Mode <span style={{ color: C.danger }}>*</span></label>
            <Select value={selectedPaymentmethodType || { value: 'cash', label: 'Cash' }} onChange={handlePaymentTypeChange} options={paymentmethodOptions}
              placeholder="Select mode…" isSearchable menuPortalTarget={document.body} styles={selectStyles} />
          </div>
          <div>
            <label style={lbl}>Bank</label>
            <Select value={selectedBank} onChange={handleBankChange} options={bankOptions}
              placeholder="Select bank…" isSearchable isDisabled={isCashPayment} menuPortalTarget={document.body} styles={selectStyles} />
          </div>
          <div>
            <label style={lbl}>{selectedPaymentmethodType?.value === 3 ? 'Cheque No' : 'Reference No'}</label>
            <input type="text" name="ChequeNumber" value={formData.ChequeNumber || ''} onChange={handleChange}
              disabled={isCashPayment} style={{ ...inp, backgroundColor: isCashPayment ? '#f0f4f4' : '#f8fafa' }} />
          </div>
          <div>
            <label style={lbl}>{selectedPaymentmethodType?.value === 3 ? 'Cheque Date' : 'Reference Date'}</label>
            <input type="date" name="ChequeDate" value={formatDateForInput(formData.ChequeDate)} onChange={handleChange}
              disabled={isCashPayment} style={{ ...inp, backgroundColor: isCashPayment ? '#f0f4f4' : '#f8fafa' }} />
          </div>
          <div>
            <label style={lbl}>{selectedPaymentmethodType?.value === 3 ? 'Cheque Details' : 'Reference Details'}</label>
            <input type="text" name="chequeDetails" value={formData.chequeDetails || ''} onChange={handleChange}
              disabled={isCashPayment} style={{ ...inp, backgroundColor: isCashPayment ? '#f0f4f4' : '#f8fafa' }} />
          </div>
        </div>
      </div>

    </div>
  );
};

export default PaymentAndReceiptCreate;
